/**
 * variantHelpers.ts
 *
 * Safe accessors for the `service.variants.variants` structure used across
 * the marketplace UI. Historically several components read
 * `service.variants.variants.length` without guarding the nested shape; a
 * service row can have `pricing_model === 'variants'` while the underlying
 * `variants` JSONB stores a different pricing shape (e.g. `tiered_per_seat`,
 * `line_plus_box`). In that case `service.variants.variants` is undefined and
 * any `.length` / `.map` access throws at render time, tripping the
 * ErrorBoundary and producing the generic "Something went wrong" page.
 *
 * Rule going forward: every component that consumes
 * `service.variants.variants` MUST go through `getVariantList(service)` (or
 * `hasRenderableVariants(service)`). New direct access to
 * `service.variants.variants.<member>` is blocked by the CI guard
 * (`scripts/ci/guard-no-unsafe-variants-access.sh`).
 */

type AnyRecord = Record<string, any>;

/**
 * Return the inner variants array when it's usable for UI rendering.
 * Always returns an array (possibly empty). Never throws.
 */
export function getVariantList(service: AnyRecord | null | undefined): any[] {
  if (!service || typeof service !== 'object') return [];
  const v = (service as AnyRecord).variants;
  if (!v || typeof v !== 'object') return [];
  const inner = (v as AnyRecord).variants;
  return Array.isArray(inner) ? inner : [];
}

/**
 * True when the service has a real variants array the standard
 * variant-based components can render.
 *
 * Note: this does NOT check `pricing_model` — a service can advertise
 * `pricing_model = 'variants'` but have a non-standard shape (tiered, etc.).
 * Components that need "real variants" should call this helper rather than
 * trusting `pricing_model` alone.
 */
export function hasRenderableVariants(
  service: AnyRecord | null | undefined,
): boolean {
  return getVariantList(service).length > 0;
}

/**
 * True when the service both claims `pricing_model='variants'` at the
 * top level AND actually carries a renderable variants array.
 */
export function usesVariantPricing(
  service: AnyRecord | null | undefined,
): boolean {
  if (!service) return false;
  if ((service as AnyRecord).pricing_model !== 'variants') return false;
  return hasRenderableVariants(service);
}

/**
 * Find a variant by key (respects `default_variant_key` if no explicit
 * key provided). Returns undefined when no variant list or no match.
 */
export function findVariant(
  service: AnyRecord | null | undefined,
  key?: string | null,
): AnyRecord | undefined {
  const list = getVariantList(service);
  if (list.length === 0) return undefined;
  const target = key ?? (service as AnyRecord | null)?.default_variant_key;
  if (target) {
    const found = list.find((v: AnyRecord) => v?.key === target);
    if (found) return found;
  }
  return list[0];
}
